import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle } from "lucide-react";
import logo from "../../resources/Color variable-07.png";
import { auth } from "../../firebase/firebase";
import {
  logInWithEmailAndPassword,
  sendPasswordReset,
} from "../../firebase/firebase.user";
import {
  setUserCredetials,
  setUserDetails,
} from "../../store/user/user.reducer";
import usePumble from "../../hooks/pumble/usePumble";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState(null);

  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sendLogin } = usePumble();

  const userCredentials = useSelector((state) => state.user?.userCredentials);
  const userDetails = useSelector((state) => state.user?.userDetails);

  // Redirect based on role
  useEffect(() => {
    if (user && userDetails) {
      if (userDetails.role === "owner" || userDetails.role === "user") {
        navigate("/finanzas");
      } else if (userDetails.role === "patient") {
        navigate(`/self/${userDetails.patientRecordId}`);
      } else if (userDetails.role === "admin") {
        navigate(`/admin`);
      }
    }
  }, [user, userDetails, navigate]);

  // Set user credentials
  useEffect(() => {
    if (user && userCredentials === null) {
      let deserializedUser = JSON.parse(JSON.stringify(user));
      dispatch(setUserCredetials(deserializedUser));
    }
  }, [user, userCredentials, dispatch]);

  const logIn = async () => {
    if (!email || !password) {
      setError("Por favor completa todos los campos");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const loginUserData = await logInWithEmailAndPassword(email, password);
      if (loginUserData) {
        dispatch(setUserDetails(loginUserData));
        sendLogin(`Inicio de Sesion de ${loginUserData?.email}`);
      }
    } catch (err) {
      setError("Email o contraseña incorrectos");
    } finally {
      setIsLoading(false);
    }
  };

  const passwordReset = async () => {
    if (!email) {
      setError("Por favor ingresa tu email");
      return;
    }

    setError(null);
    try {
      await sendPasswordReset(email);
      setResetSent(true);
      setTimeout(() => {
        setResetSent(false);
        setShowResetPassword(false);
      }, 3000);
    } catch (err) {
      setError("Error al enviar el email de recuperación");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isLoading) {
      logIn();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8 flex flex-col justify-center">
          <img
            src={logo}
            className="object-contain h-20 w-auto"
            alt="OdontoStark"
          />

          <h1 className="text-3xl font-bold text-slate-900 mb-2 mt-10">
            Bienvenido de vuelta
          </h1>
          <p className="text-slate-600">Inicia sesión para continuar</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {resetSent && (
            <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-700">
                Email de recuperación enviado. Revisa tu bandeja de entrada.
              </p>
            </div>
          )}

          <div className="space-y-4">
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="tu@email.com"
                  className="block w-full pl-11 pr-4 py-3 rounded-xl border-2 border-slate-200 transition-all text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500/20"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  id="password"
                  type={isPasswordVisible ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="••••••••"
                  className="block w-full pl-11 pr-12 py-3 rounded-xl border-2 border-slate-200 transition-all text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500/20"
                />
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  {isPasswordVisible ? (
                    <EyeOff className="w-5 h-5 text-slate-500" />
                  ) : (
                    <Eye className="w-5 h-5 text-slate-500" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            {!showResetPassword && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            )}

            {/* Reset Password Section */}
            {showResetPassword && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-slate-700 mb-3">
                  Ingresa tu email y te enviaremos un enlace para restablecer tu
                  contraseña.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={passwordReset}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                  >
                    Enviar enlace
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(false)}
                    className="px-4 py-2 bg-white text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors text-sm border border-slate-200"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Login Button */}
            <button
              type="button"
              onClick={logIn}
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                !isLoading
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-600/30 hover:shadow-xl hover:scale-105"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Iniciando sesión...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  Iniciar sesión
                  <ArrowRight className="w-5 h-5" />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Sign Up Link */}
        <div className="text-center mt-6">
          <p className="text-slate-600">
            ¿No tienes una cuenta?{" "}
            <Link
              to="/registrarse"
              className="font-semibold text-blue-600 hover:text-blue-700 transition-colors"
            >
              Crear cuenta gratis
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
